function hexToDec(h: string) {
    return parseInt(h, 16);
}

function decToHex(n: number) {
    let h = n.toString(16);
    if (h.length === 1) {
        return '0' + h;
    }
    return h;
}

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
}

// https://css-tricks.com/converting-color-spaces-in-javascript/
export function rgbToHsl(r: number, g: number, b: number) {
    // Make r, g, and b fractions of 1
    r = r / 255;
    g = g / 255;
    b = b / 255;

    // Find greatest and smallest channel values
    let cmin = Math.min(r,g,b),
        cmax = Math.max(r,g,b),
        delta = cmax - cmin,
        h = 0,
        s = 0,
        l = 0;
    
    // Calculate hue
    // No difference
    if (delta === 0)
    h = 0;
    // Red is max
    else if (cmax === r)
    h = ((g - b) / delta) % 6;
    // Green is max
    else if (cmax === g)
    h = (b - r) / delta + 2;
    // Blue is max
    else
    h = (r - g) / delta + 4;

    h = Math.round(h * 60);

    // Make negative hues positive behind 360°
    if (h < 0)
        h += 360;
    
    // Calculate lightness
    l = (cmax + cmin) / 2;

    // Calculate saturation
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
        
    // Multiply l and s by 100
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    // return "hsl(" + h + "," + s + "%," + l + "%)";
    return {
        hue: h,
        saturation: s,
        lightness: l
    };
}

export function hslToRgb(h: number, s: number, l: number) {
    // Must be fractions of 1
    s /= 100;
    l /= 100;

    let c = (1 - Math.abs(2 * l - 1)) * s,
        x = c * (1 - Math.abs((h / 60) % 2 - 1)),
        m = l - c/2,
        r = 0,
        g = 0,
        b = 0;
    
    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;  
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    return {
        red: r,
        green: g,
        blue: b
    };
}

export class Color {
    public readonly red: number;
    public readonly gre: number;
    public readonly blu: number;
    public readonly opa: number;

    static fromHex(hex: string, opacity?: number) {
        if (hex === "transparent") {
            return Color.transparent();
        }
        /**
         * A valid hex can be formed from 3, 4, 6 or 8 characters from 0 to F, case insensitive.
         * The long form (8 characters also contains opacity), 00 (numeric 0) is fully transparent, FF (numeric 255) is fully opaque
         * Short format hex, 3 characters for example #4A9 is the short form for #44AA99
         * Short format hexa, 4 characters for example #4A9B is the short form for #44AA99BB
         */
        if (!/^#?(([A-Fa-f0-9]{3}){1,2}|(([A-Fa-f0-9]{4}){1,2}))$/.test(hex)) {
            throw new Error('Bad color Hex code');
        }
        if (hex[0] === "#") hex = hex.substring(1);
        let c: string[] = hex.split('');
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        if (c.length === 4) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2], c[3], c[3]];
        }
        const red = hexToDec('' + c[0] + c[1]);
        const gre = hexToDec('' + c[2] + c[3]);
        const blu = hexToDec('' + c[4] + c[5]);
        const opa255 = c.length === 8 ? hexToDec('' + c[6] + c[7]) : 255;
        const opa = opacity ? opacity : Math.round(10000 * opa255 / 255) / 10000;
        return new Color(red, gre, blu, opa);
    }

    static transparent() {
        return new Color(127, 127, 127, 0);
    }

    constructor(r: number, g: number, b: number, o: number = 1) {
        this.red = clamp(r, 0, 255);
        this.gre = clamp(g, 0, 255);
        this.blu = clamp(b, 0, 255);
        this.opa = clamp(o, 0, 1);
    }

    rgb() {
        return 'rgba(' + [this.red, this.gre, this.blu].join(', ') + ')';
    }

    rgba() {
        return 'rgba(' + [this.red, this.gre, this.blu, this.opa].join(', ') + ')';
    }

    hex(withHashtag = true) {
        return (withHashtag ? '#' : '') + decToHex(this.red) + decToHex(this.gre) + decToHex(this.blu);
    }

    hexa(withHashtag = true) {
        return (withHashtag ? '#' : '') + decToHex(this.red) + decToHex(this.gre) + decToHex(this.blu) + decToHex(Math.round(this.opa * 255));
    }

    /**
     * I am computing the distance between 2 colors using a simple Euclidian distance in RGB space even if RGB is not perceptually uniform,
     * because I only want to see how dark or how light a color is, to determine if I can lighten or darken it for the hover effect.
     * https://colorjs.io/docs/color-difference.html
     * In RGB space, the greatest distance possible is 441.6729559300637 from white (255, 255, 255) to black (0, 0, 0).
     * 
     * So, for the hover effect, if the button is white, I cannot lighen the color white so I will darken it (making it a light gray)
     * 
     * Another way of determining how bright or dark a color is, is to convert rgb to hsl space and use lightness
     * which we know is a percentual value (from 0 to 100%)
     * 
     * Usage:
     * To compute the distance from black: color.distanceFrom(new Color(0, 0, 0))
     * To compute the distance from white: color.distanceFrom(new Color(255, 255, 255))
     */
    distanceFrom(c: Color) {
        return Math.sqrt(Math.pow(c.red - this.red, 2) + Math.pow(c.gre - this.gre, 2) + Math.pow(c.blu - this.blu, 2));
    }

    private adjustColorLightness(amount: number) {
        if (this.opa === 0) {
            if (amount > 0) {
                return new Color(255, 255, 255, amount / 100);
            }
            if (amount < 0) {
                return new Color(0, 0, 0, amount / 100);
            }
        }
        // return new Color(this.red + amount, this.gre + amount, this.blu + amount);
        const hslData = rgbToHsl(this.red, this.gre, this.blu);
        hslData.lightness = clamp(hslData.lightness + amount, 0, 100);
        const rgbData = hslToRgb(hslData.hue, hslData.saturation, hslData.lightness);
        return new Color(rgbData.red, rgbData.green, rgbData.blue, this.opa);
    }

    lighten(amount: number) {
        return this.adjustColorLightness(Math.abs(amount));
    }

    darken(amount: number) {
        return this.adjustColorLightness(-Math.abs(amount));
    }

    private adjustColorSaturation(amount: number) {
        const hslData = rgbToHsl(this.red, this.gre, this.blu);
        hslData.saturation = clamp(hslData.saturation + amount, 0, 100);
        const rgbData = hslToRgb(hslData.hue, hslData.saturation, hslData.lightness);
        return new Color(rgbData.red, rgbData.green, rgbData.blue, this.opa);
    }

    saturate(amount: number) {
        return this.adjustColorSaturation(Math.abs(amount));
    }

    desaturate(amount: number) {
        return this.adjustColorSaturation(-Math.abs(amount));
    }

    private adjustTransparency(amount: number) {
        const opacity = clamp(this.opa + amount, 0, 1);
        return new Color(this.red, this.gre, this.blu, opacity);
    }

    fadeIn(amount: number) {
        return this.adjustTransparency(Math.abs(amount));
    }
    
    fadeOut(amount: number) {
        return this.adjustTransparency(-Math.abs(amount));
    }
}